import { create } from 'zustand';
import { authService, setMemoryToken } from '../services/api';
import { User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    telephone?: string;
    role: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      console.log('[AUTH] Tentative de connexion...', email);
      const response = await authService.login(email, password);
      console.log('[AUTH] Réponse:', response.data);
      
      const { access_token, refresh_token, user } = response.data;
      
      if (!access_token) {
        console.error('[AUTH] Pas de token dans la réponse!');
        set({ error: 'Erreur: pas de token reçu', isLoading: false });
        return false;
      }
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Stocker aussi en mémoire
      setMemoryToken(access_token);
      
      // Vérification immédiate
      const storedToken = localStorage.getItem('access_token');
      console.log('[AUTH] Token stocké:', storedToken?.substring(0, 20) + '...');
      console.log('[AUTH] Vérification localStorage:', storedToken === access_token ? 'OK' : 'ERREUR!');
      
      set({ user, isAuthenticated: true, isLoading: false });
      return true;
    } catch (error: any) {
      console.error('[AUTH] Erreur:', error);
      let message = 'Erreur de connexion';
      
      if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = 'Délai de connexion dépassé. Vérifiez votre connexion internet et réessayez.';
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        message = 'Impossible de joindre le serveur. Vérifiez votre connexion internet.';
      } else if (error.message) {
        message = `Erreur: ${error.message}`;
      }
      
      set({ error: message, isLoading: false });
      return false;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await authService.register(data);
      set({ isLoading: false });
      return true;
    } catch (error: any) {
      console.error('[AUTH] Erreur inscription:', error);
      let message = 'Erreur d\'inscription';
      
      if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = 'Délai de connexion dépassé. Vérifiez votre connexion internet et réessayez.';
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        message = 'Impossible de joindre le serveur. Vérifiez votre connexion internet.';
      } else if (error.message) {
        message = `Erreur: ${error.message}`;
      }
      
      set({ error: message, isLoading: false });
      return false;
    }
  },

  logout: async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignorer les erreurs de logout
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setMemoryToken(null);
      set({ user: null, isAuthenticated: false });
    }
  },

  checkAuth: async () => {
    // Si déjà authentifié, ne pas revérifier
    if (get().isAuthenticated && get().user) {
      console.log('[AUTH] checkAuth - déjà authentifié, skip');
      set({ isLoading: false });
      return;
    }
    
    const token = localStorage.getItem('access_token');
    console.log('[AUTH] checkAuth - token existe:', !!token);
    
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      console.log('[AUTH] checkAuth - appel /me...');
      const response = await authService.getMe();
      console.log('[AUTH] checkAuth - succès:', response.data);
      set({ 
        user: response.data.user, 
        isAuthenticated: true, 
        isLoading: false 
      });
    } catch (error: any) {
      console.error('[AUTH] checkAuth - erreur:', error.response?.status, error.response?.data);
      // Ne pas effacer si c'est une erreur réseau
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setMemoryToken(null);
        set({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false 
        });
      } else {
        // Erreur réseau, garder le token
        set({ isLoading: false });
      }
    }
  },

  updateUser: (data) => {
    const currentUser = get().user;
    if (currentUser) {
      set({ user: { ...currentUser, ...data } });
    }
  },

  clearError: () => set({ error: null }),
}));
